import { CardAction, CardDefinition, Order, TemplateEnum } from '@barthauer/abc/cards-view';
import { CompareHelper, ConvertHelper } from '@barthauer/abc/core';
import { BCEDocument, BCEElement, BCEFolder, BCEGroup, BCEObject, BCETemplate } from '../models/bce.model';
import { ResourceSet } from '@barthauer/abc/core/resources';

export class CardHelper {
  /**
   * Recursively calculates the total number of documents within a card definition,
   * including those nested inside subfolders.
   * @param cardDefinition The card definition (Group or Folder) to count documents from.
   * @returns The total count of documents.
   */
  public static getDocumentCount(cardDefinition: CardDefinition): number {
    let count: number = 0;
    cardDefinition.CardDefinitions?.forEach((cd) => {
      if (cd instanceof DocumentCard) {
        count++;
      } else if (cd instanceof FolderCard) {
        count += this.getDocumentCount(cd);
      }
    });
    return count;
  }
}

export class GroupCard implements CardDefinition {
  Icon: string;
  DisplayText: string;
  SecondaryDisplayText: string;
  Picture: string;
  Description: string;
  IsGroup: boolean = true;
  PrimaryAction: CardAction;
  SecondaryAction: CardAction;
  ThirdAction: CardAction;
  FourthAction: CardAction;
  OtherActions: CardAction[];
  CardDefinitions: CardDefinition[] = [];
  Template: TemplateEnum;
  Order: Order;
  DataObject: BCEGroup;

  constructor(res: ResourceSet, data: BCEGroup, factory: (obj: BCEObject) => CardDefinition) {
    this.DisplayText = data.Designation;
    this.Description = data.Description;
    this.Template = TemplateEnum.Large;
    this.DataObject = new BCEGroup(data);
    data.Childs?.sort((a, b) => CompareHelper.compare(a.Designation, b.Designation))?.forEach((element) => {
      const card = factory(element);
      if (card) {
        this.CardDefinitions.push(card);
      }
    });
    const docCount = CardHelper.getDocumentCount(this);
    const folderCount = this.CardDefinitions.filter((cd) => cd instanceof FolderCard).length;
    const docLabel = res.get('Doc-Abbreviation');
    const folderLabel = res.get('Subfolders');
    this.SecondaryDisplayText = `${docCount} ${docLabel}  •  ${folderCount} ${folderLabel} `;
  }

  IsVisible(search: string): boolean {
    return search ? false : true;
  }
}

export class FolderCard implements CardDefinition {
  Icon: string;
  DisplayText: string;
  SecondaryDisplayText: string;
  Description: string;
  headerDescription: boolean = true;
  Picture: string;
  PrimaryAction?: CardAction;
  IsFolder: boolean = true;
  SecondaryAction?: CardAction;
  ThirdAction?: CardAction;
  FourthAction?: CardAction;
  OtherActions: CardAction[];
  CardDefinitions: CardDefinition[] = [];
  Template: TemplateEnum;
  Order: Order;
  DataObject: BCEFolder;

  constructor(res: ResourceSet, data: BCEFolder, factory: (obj: BCEObject) => CardDefinition) {
    this.DisplayText = data.Designation;
    this.Description = data.Description || res.get('No-Description');
    this.Template = TemplateEnum.Small;
    this.DataObject = new BCEFolder(data);
    data.Childs?.sort((a, b) => CompareHelper.compare(a.Designation, b.Designation))?.forEach((element) => {
      const card = factory(element);
      if (card) {
        this.CardDefinitions.push(card);
      }
    });
    const docCount = CardHelper.getDocumentCount(this);
    const folderCount = this.CardDefinitions.filter((cd) => cd instanceof FolderCard).length;
    const docLabel = res.get('Doc-Abbreviation');
    const folderLabel = res.get('Subfolders');
    this.SecondaryDisplayText = `${docCount} ${docLabel}  •  ${folderCount} ${folderLabel} || img:assets/resources/abc-management-configuration-explorer/folder.svg | ${docCount} ${docLabel}  •  ${folderCount} ${folderLabel}`;
  }
  IsVisible(search: string): boolean {
    return search ? false : true;
  }
}

export abstract class DocumentCardBase<T extends BCEElement> implements CardDefinition {
  Icon: string;
  DisplayText: string;
  SecondaryDisplayText: string;
  Description: string;
  Picture: string;
  IsFolder: boolean = false;
  PrimaryAction: CardAction;
  SecondaryAction: CardAction;
  ThirdAction: CardAction;
  FourthAction: CardAction;
  OtherActions: CardAction[];
  Template: TemplateEnum;
  Order: Order;
  DataObject: T;
  constructor(res: ResourceSet, data: T) {
    this.DisplayText = data.Designation;
    this.Description = data.Description || res.get('No-Description');
    this.Template = TemplateEnum.Small;
    this.DataObject = data;
    if (data.SysDateUpdate) {
      const changedLabel = res.get('Changed', 'Changed');
      const dateText = ConvertHelper.toLocaleFormattedDateString(data.SysDateUpdate);
      this.SecondaryDisplayText = `${changedLabel} || img:assets/resources/abc-management-configuration-explorer/document.svg | ${changedLabel} || img:assets/resources/abc-management-configuration-explorer/document.svg | ${dateText}`;
    }
  }
  IsVisible(search: string): boolean {
    return search ? true : false;
  }
}

export class DocumentCard extends DocumentCardBase<BCEDocument> {
  constructor(res: ResourceSet, data: BCEDocument) {
    super(res, data);
  }
}

export class TemplateCard extends DocumentCardBase<BCETemplate> {
  constructor(res: ResourceSet, data: BCETemplate) {
    super(res, data);
  }
}
